import { useState, useEffect, ChangeEvent, useCallback } from "react";
import DealerList from "../dealer-list/DealerList";
import DealerListSearch from "../dealer-list/DealerListSearch";
import CustomPagination from "../../common/CustomPagination";
import DealerTableHeader from "../dealer-list/DealerTableHeader";
import DealerTableBody from "../dealer-list/DealerTableBody";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { SilentRequest } from "@azure/msal-browser";
import { Company } from "../../data/interfaces/company";
import { getCompaniesAsync } from "../../data/companyService";
import CustomContainer from "../../common/CustomContainer";
import { PrimaryButton } from "../../Styles/Styles";
import { useNavigate } from "react-router-dom";

type Claims = {
  roles: string[];
};

const HomePage = () => {
  const [canCreateCompanies, setCanCreateCompanies] = useState<boolean>(false);
  const [fullCompaniesList, setFullCompaniesList] = useState<Company[]>([]);
  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);

  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  const endIndex: number = pageNumber * pageSize;
  const startIndex: number = endIndex - pageSize;
  const currentPage: Company[] = companiesList.slice(startIndex, endIndex);
  const lastPage: number = Math.ceil(companiesList.length / pageSize);
  const disablePrevious: boolean = pageNumber === 1 ? true : false;
  const disableNext: boolean = currentPage.length < pageSize ? true : false;

  const getCompanies = useCallback(async () => {
    if (isAuthenticated) {
      const account = instance.getAllAccounts()[0];
      const silentRequest: SilentRequest = {
        scopes: ["api://a22e7296-106f-4a00-af8d-f86edb386a1b/api_access"],
        account: account,
      };

      const token = await instance.acquireTokenSilent(silentRequest);
      setIsLoading(true);
      const companies = await getCompaniesAsync(token.accessToken);

      setCompaniesList(companies);
      setFullCompaniesList(companies);

      setIsLoading(false);
    }
  }, [instance, isAuthenticated, setIsLoading]);

  useEffect(() => {
    if (isAuthenticated) 
    {
      const claims: Claims | undefined = instance.getActiveAccount()
        ?.idTokenClaims as Claims;

      if (claims.roles !== undefined) {
        const allowedToCreate = claims.roles.includes("CompanyCreator");
        setCanCreateCompanies(allowedToCreate);
      }
    }
    getCompanies();

  }, [isAuthenticated, instance, getCompanies]);

  const handleSearchChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const searchCriteria = event.target.value.trim();
    const companies = fullCompaniesList;

    const results = companies.filter(
      (company) =>
        company.accountNumber === Number(searchCriteria) ||
        company.companyName
          .toLocaleLowerCase()
          .includes(searchCriteria.toLocaleLowerCase())
    );

    setCompaniesList(results);
  };

  const next = () => {
    let nextPageNumber = pageNumber + 1;

    setPageNumber(nextPageNumber);
  };

  const previous = () => {
    let previousPageNumber = pageNumber - 1;
    setPageNumber(previousPageNumber);
  };

  return (
    <CustomContainer>
      {isAuthenticated === false ? (
        <div className="text-center p-1">
          <h2>You must login to use this app.</h2>
        </div>
      ) : isLoading === false ? (
        <>
          <h2 className="display-6 p-1 m-1">Active Company List</h2>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <DealerListSearch handleSearchChange={handleSearchChange} />

      {canCreateCompanies && (<PrimaryButton
              style={{
                margin: "1%",
              }}
              className="btn btn-sm"
              onClick={() => {
                navigate("/newcustomer");
              }}
            >
              Add Company
            </PrimaryButton>)}

          </div>
          <DealerList>
            <DealerTableHeader />
            <DealerTableBody companies={currentPage} />
          </DealerList>
          <CustomPagination
            nextPage={next}
            previousPage={previous}
            currentPage={pageNumber}
            lastPage={lastPage}
            disableNextButton={disableNext}
            disablePreviousButton={disablePrevious}
          />
        </>
      ) : (
        <div className="text-center p-1">
          <h2>Loading...</h2>
        </div>
      )}
    </CustomContainer>
  );
};

export default HomePage;
